import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Flex,
  Heading,
  HStack,
  IconButton,
  Image,
  SimpleGrid,
  Stack,
  Tag,
  Text,
  useColorModeValue,
  Collapse,
} from "@chakra-ui/react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";

/* --------------------------
   Small reusable components
-------------------------- */


// Put this helper near your InfoCard components (or inside HomePage.jsx)
function ExpandableInfoCard({ label, summary, fullText }) {
  const [open, setOpen] = useState(false);
  const linkColor = useColorModeValue("#16A34A", "#86EFAC"); // green
  const btnBg = useColorModeValue("rgba(34,197,94,0.10)", "rgba(34,197,94,0.16)");

  return (
    <InfoCard
      label={label}
      text={
        <>
          {/* Short summary (always visible) */}
          <Text lineHeight={1.75}>{summary}</Text>

          {/* Full text (collapsible) */}
          <Collapse in={open} animateOpacity>
            <Box mt={4}>
              <Text lineHeight={1.75}>{fullText}</Text>
            </Box>
          </Collapse>

          {/* Toggle */}
          <HStack mt={5}>
            <Button
              size="sm"
              borderRadius="full"
              bg={btnBg}
              color={linkColor}
              fontWeight="900"
              _hover={{ filter: "brightness(0.98)" }}
              onClick={() => setOpen((v) => !v)}
            >
              {open ? "Show less" : "Read more"}
            </Button>
          </HStack>
        </>
      }
    />
  );
}

function Section({ title, subtitle, children }) {
  const titleColor = useColorModeValue("#0F172A", "white");
  const subColor = useColorModeValue("gray.600", "gray.300");
  const divider = useColorModeValue("rgba(2,6,23,0.08)", "rgba(148,163,184,0.18)");

  return (
    <Box py={{ base: 4, md: 6 }}>
      <Container maxW="1200px">
        <Stack spacing={2} mb={{ base: 6, md: 8 }}>
          <Heading fontSize={{ base: "2xl", md: "3xl" }} color={titleColor} letterSpacing="tight">
            {title}
          </Heading>
          {subtitle ? (
            <Text fontSize={{ base: "md", md: "lg" }} color={subColor} maxW="900px">
              {subtitle}
            </Text>
          ) : null}
        </Stack>

        {children}

        <Divider mt={{ base: 10, md: 14 }} borderColor={divider} />
      </Container>
    </Box>
  );
}

function InfoCard({ label, text }) {
  const cardBg = useColorModeValue("white", "rgba(255,255,255,0.04)");
  const border = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");
  const bodyColor = useColorModeValue("gray.700", "gray.200");

  return (
    <Box
      bg={cardBg}
      border="1px solid"
      borderColor={border}
      borderRadius="22px"
      p={{ base: 6, md: 7 }}
      boxShadow={useColorModeValue("0 12px 30px rgba(0,0,0,0.08)", "none")}
    >
      <HStack spacing={3} mb={3}>
        <Tag
          size="lg"
          borderRadius="full"
          bg={useColorModeValue("rgba(34,197,94,0.12)", "rgba(34,197,94,0.18)")}
          color={useColorModeValue("#166534", "#86EFAC")}
          fontWeight="900"
          letterSpacing="0.06em"
        >
          {label}
        </Tag>
      </HStack>

      <Text fontSize={{ base: "md", md: "lg" }} color={bodyColor} textAlign="left" lineHeight={1.75}>
        {text}
      </Text>
    </Box>
  );
}

function StatPill({ k, v }) {
  const border = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");
  const bg = useColorModeValue("rgba(255,255,255,0.70)", "rgba(255,255,255,0.04)");
  const kColor = useColorModeValue("gray.600", "gray.300");
  const vColor = useColorModeValue("#0F172A", "white");

  return (
    <Box border="1px solid" borderColor={border} bg={bg} borderRadius="18px" p={4}>
      <Text fontSize="xs" letterSpacing="0.12em" fontWeight="900" color={kColor}>
        {k.toUpperCase()}
      </Text>
      <Text fontSize="sm" fontWeight="800" color={vColor} mt={1}>
        {v}
      </Text>
    </Box>
  );
}

/* --------------------------
   Inline Carousel (no imports)
   - works for desktop and mobile
   - no cropping: objectFit="contain"
-------------------------- */
function InlineCarousel({
  images,
  title,
  subtitle,
  autoPlay = true,
  intervalMs = 4500,
  aspect = { base: "16 / 10", md: "16 / 8" },
  dotOnColor = "#22C55E",
}) {
  const [idx, setIdx] = useState(0);

  const border = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");
  const panelBg = useColorModeValue("white", "rgba(255,255,255,0.03)");
  const titleColor = useColorModeValue("#0F172A", "white");
  const subColor = useColorModeValue("gray.600", "gray.300");
  const dotOff = useColorModeValue("rgba(2,6,23,0.18)", "rgba(255,255,255,0.22)");
  const dotOn = dotOnColor;

  const n = images?.length || 0;

  useEffect(() => {
    if (!autoPlay || n <= 1) return;
    const t = setInterval(() => setIdx((v) => (v + 1) % n), intervalMs);
    return () => clearInterval(t);
  }, [autoPlay, intervalMs, n]);

  const prev = () => setIdx((v) => (v - 1 + n) % n);
  const next = () => setIdx((v) => (v + 1) % n);

  if (!n) return null;

  const current = images[idx];

  return (
    <Box
      border="1px solid"
      borderColor={border}
      borderRadius="26px"
      overflow="hidden"
      bg={panelBg}
      boxShadow={useColorModeValue("0 14px 40px rgba(0,0,0,0.10)", "none")}
    >
      <Box p={{ base: 4, md: 6 }}>
        <Heading fontSize={{ base: "lg", md: "xl" }} color={titleColor} letterSpacing="tight">
          {title}
        </Heading>
        {subtitle ? (
          <Text color={subColor} mt={1}>
            {subtitle}
          </Text>
        ) : null}
      </Box>

      <Divider borderColor={border} />

      <Box position="relative" p={{ base: 3, md: 4 }}>
        <Box
          border="1px solid"
          borderColor={border}
          borderRadius="22px"
          bg={useColorModeValue("rgba(2,6,23,0.02)", "rgba(255,255,255,0.03)")}
          overflow="hidden"
        >
          <Box
            w="100%"
            sx={{
              aspectRatio: aspect,
            }}
          >
            <Flex w="100%" h="100%" align="center" justify="center" p={{ base: 3, md: 5 }}>
              <Image
                src={typeof current === "string" ? current : current.src}
                alt={typeof current === "string" ? `slide-${idx + 1}` : current.alt || `slide-${idx + 1}`}
                w="100%"
                h="100%"
                objectFit="contain"
                draggable={false}
              />
            </Flex>
          </Box>
        </Box>

        {n > 1 && (
          <>
            <IconButton
              aria-label="Previous"
              icon={<ChevronLeftIcon />}
              position="absolute"
              left={{ base: 2, md: 4 }}
              top="50%"
              transform="translateY(-50%)"
              onClick={prev}
              variant="ghost"
              bg={useColorModeValue("rgba(255,255,255,0.70)", "rgba(15,23,42,0.55)")}
              _hover={{ filter: "brightness(0.97)" }}
              borderRadius="999px"
            />
            <IconButton
              aria-label="Next"
              icon={<ChevronRightIcon />}
              position="absolute"
              right={{ base: 2, md: 4 }}
              top="50%"
              transform="translateY(-50%)"
              onClick={next}
              variant="ghost"
              bg={useColorModeValue("rgba(255,255,255,0.70)", "rgba(15,23,42,0.55)")}
              _hover={{ filter: "brightness(0.97)" }}
              borderRadius="999px"
            />
          </>
        )}

        {n > 1 && (
          <HStack justify="center" spacing={2} pt={4}>
            {images.map((_, i) => (
              <Box
                key={i}
                w="8px"
                h="8px"
                borderRadius="999px"
                bg={i === idx ? dotOn : dotOff}
                cursor="pointer"
                onClick={() => setIdx(i)}
              />
            ))}
          </HStack>
        )}
      </Box>
    </Box>
  );
}

/* --------------------------
   Home Page (Nextcon–SCVIC)
-------------------------- */
export default function HomePage() {
  const isMobile = useSelector((state) => state.windowSize.isMobile);
  const location = useLocation();

  // EN/FR base prefix
  const lang = location.pathname.startsWith("/fr") ? "fr" : "en";
  const base = lang === "fr" ? "/fr" : "/en";

  // ✅ GREEN brand palette
  const accent = "#16A34A"; // green-600
  const accent2 = "#22C55E"; // green-500

  const titleColor = useColorModeValue("#0F172A", "white");
  const subColor = useColorModeValue("gray.600", "gray.300");
  const softBorder = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");
  const pageBg = useColorModeValue("white", "#0B1220");
  const panelBg = useColorModeValue("white", "rgba(255,255,255,0.03)");

  // Green-tinted hero background
  const heroBg = useColorModeValue(
    "radial-gradient(1000px 450px at 15% 10%, rgba(34,197,94,0.18), transparent 60%), radial-gradient(900px 450px at 85% 30%, rgba(22,163,74,0.12), transparent 55%), linear-gradient(180deg, rgba(15,23,42,0.02), rgba(15,23,42,0.00))",
    "radial-gradient(1000px 450px at 15% 10%, rgba(34,197,94,0.20), transparent 60%), radial-gradient(900px 450px at 85% 30%, rgba(22,163,74,0.14), transparent 55%), linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.00))"
  );

  // ✅ Put your real images here (public folder recommended)
  const highlights = useMemo(
    () => [
      { src: "/slider1.jpg", alt: "Nextcon–SCVIC highlight 1" },
      { src: "/sld 4.png", alt: "Nextcon–SCVIC highlight 2" },
      { src: "/slider2.jpg", alt: "Nextcon–SCVIC highlight 3" },
      { src: "/sld 3.png", alt: "Nextcon–SCVIC highlight 3" },
      { src: "/wmremove-transformed (2).png", alt: "Nextcon–SCVIC highlight 3" }
    ],
    []
  );

  const labPictures = useMemo(
    () => [
      { src: "/sld 5.png", alt: "Nextcon lab photo 2" },
      { src: "/dean 4.jpeg", alt: "Nextcon lab photo 2" },
      { src: "/new1.jpg", alt: "Nextcon lab photo 1" },
      { src: "/sld 1.jpg", alt: "Nextcon lab photo 1" },
      { src: "/sld 6.png", alt: "Nextcon lab photo 3" },
    ],
    []
  );

  return (
    <Box bg={pageBg}>
      {/* HERO */}
      <Box
        pt={{ base: 8, md: 12 }}
        pb={{ base: 10, md: 14 }}
        bg={heroBg}
        borderBottom="1px solid"
        borderColor={softBorder}
      >
        <Container maxW="1200px">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 8, md: 12 }} alignItems="center">
            <Stack spacing={5}>
              <HStack spacing={2} flexWrap="wrap">
                <Tag
                  size="lg"
                  borderRadius="full"
                  bg={useColorModeValue("white", "rgba(255,255,255,0.06)")}
                  border="1px solid"
                  borderColor={softBorder}
                  fontWeight="800"
                >
                  University of Ottawa
                </Tag>

                <Tag
                  size="lg"
                  borderRadius="full"
                  bg={useColorModeValue("rgba(34,197,94,0.14)", "rgba(34,197,94,0.18)")}
                  color={useColorModeValue("#166534", "#86EFAC")}
                  fontWeight="900"
                >
                  NEXTCON Lab • SCVIC
                </Tag>
              </HStack>

              <Heading
                color={titleColor}
                fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                lineHeight={1.05}
                letterSpacing="tight"
              >
                Next-Generation{" "}
                <Box as="span" bgClip="text" bgGradient={`linear(to-r, ${accent}, ${accent2})`}>
                  Communications & Connected Vehicles
                </Box>{" "}
                Research
              </Heading>

              <Text fontSize={{ base: "md", md: "lg" }} color={subColor} maxW="62ch" lineHeight={1.8}>
                NEXTCON Lab and the Smart Connected Vehicles Innovation Centre (SCVIC) develop 6G-ready networking,
                edge-cloud intelligence, and secure cyber-physical systems—bridging theory, testbeds, and real-world
                deployment for connected mobility and smart infrastructure.
              </Text>

              <HStack spacing={3} flexWrap="wrap">
                <Button
                  as={RouterLink}
                  to={`https://bkantarci.nextconlab.ca/publications`}
                  bg={accent2}
                  color="#06120A"
                  fontWeight="900"
                  size="lg"
                  borderRadius="16px"
                  target="_blank"
                  _hover={{ filter: "brightness(0.95)" }}
                >
                  Publications
                </Button>

                <Button
                  as={RouterLink}
                  to={`/surveys-tutorials`}
                  variant="outline"
                  borderColor={softBorder}
                  size="lg"
                  borderRadius="16px"
                  
                  fontWeight="900"
                  color={titleColor}
                  bg="#c9cfcb"
                  _hover={{ bg: useColorModeValue("rgba(2,6,23,0.05)", "rgba(255,255,255,0.06)") }}
                >
                  Surveys & Tutorials  →
                </Button>

              </HStack>

              <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={3} pt={2}>
                <StatPill k="Focus" v="6G • Edge AI • Mobility" />
                <StatPill k="Domains" v="V2X • NFV/SFC • IoT" />
                <StatPill k="Priority" v="Security • Reliability • Connectivity" />
              </SimpleGrid>
            </Stack>

            {/* Right visual: no cropping */}
            <Box
              borderRadius="26px"
              overflow="hidden"
              border="1px solid"
              borderColor={softBorder}
              bg={panelBg}
              boxShadow={useColorModeValue("0 18px 50px rgba(0,0,0,0.12)", "none")}
            >
              <Box p={{ base: 3, md: 4 }}>
                {/* Replace with your best hero image */}
                <Image src="sld 2.jpg" alt="NEXTCON Lab & SCVIC" w="100%" h="auto" objectFit="contain" />
              </Box>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

    {/* ================= FEATURED INNOVATION NETWORKS ================= */}
    <Box py={{ base: 4, md: 8 }}>
      <Container maxW="1200px">

        <Stack spacing={3} mb={{ base: 8, md: 10 }} textAlign="center">
          <Heading
            fontSize={{ base: "2xl", md: "3xl" }}
            color={titleColor}
            letterSpacing="tight"
          >
            Featured Innovation Networks
          </Heading>

          <Text
            fontSize={{ base: "md", md: "lg" }}
            color={subColor}
            maxW="820px"
            mx="auto"
            lineHeight={1.8}
          >
            Innovation Networks born out of NEXTCON Lab & SCVIC, translating advanced 6G, edge-AI, and connected mobility research into real-world intelligent and mobile networked ecosystems.
          </Text>
        </Stack>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>

          {/* ================= TRAVERSAL ================= */}
          <Box
            border="1px solid"
            borderColor={softBorder}
            borderRadius="24px"
            p={{ base: 6, md: 8 }}
            bg={useColorModeValue(
              "rgba(6,182,212,0.05)",
              "rgba(6,182,212,0.08)"
            )}
            _hover={{
              transform: "translateY(-6px)",
              transition: "0.3s ease",
              boxShadow: "0 18px 40px rgba(0,0,0,0.08)",
            }}
          >
            <Heading size="md" mb={4} color={titleColor}>
              TRAVERSAL
            </Heading>

            <Text color={subColor} mb={6} lineHeight={1.75}>
              Training and Research in Autonomous Vehicles for Reliable Services in the Air and on Land. Funded by NSERC Collaborative Research and Training Experience program.
            </Text>

            <Button
              as="a"
              href="https://traversal.nextconlab.ca/"
              target="_blank"
              size="lg"
              borderRadius="16px"
              fontWeight="900"
              bg={useColorModeValue(
                "radial-gradient(1000px 450px at 15% 10%, rgba(6,182,212,0.18), transparent 60%), radial-gradient(900px 450px at 85% 30%, rgba(34,211,238,0.14), transparent 55%), linear-gradient(180deg, rgba(15,23,42,0.02), rgba(15,23,42,0.00))",
                "radial-gradient(1000px 450px at 15% 10%, rgba(6,182,212,0.20), transparent 60%), radial-gradient(900px 450px at 85% 30%, rgba(34,211,238,0.14), transparent 55%), linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.00))"
              )}
              color={useColorModeValue("#0F172A", "white")}
              border="1px solid"
              borderColor={softBorder}
              _hover={{ filter: "brightness(0.97)" }}
            >
              Visit TRAVERSAL →
            </Button>
          </Box>

          {/* ================= SITE-CAV ================= */}
          <Box
            border="1px solid"
            borderColor={softBorder}
            borderRadius="24px"
            p={{ base: 6, md: 8 }}
            bg={useColorModeValue(
              "rgba(49,83,105,0.05)",
              "rgba(49,83,105,0.08)"
            )}
            _hover={{
              transform: "translateY(-6px)",
              transition: "0.3s ease",
              boxShadow: "0 18px 40px rgba(0,0,0,0.08)",
            }}
          >
            <Heading size="md" mb={4} color={titleColor}>
              SITE-CAV
            </Heading>

            <Text color={subColor} mb={6} lineHeight={1.75}>
              Secure, Intelligent and Trustworthy Ecosystems for Connected and Autonomous Vehicles. Funded by Ontario Research Fund-Research Excellence Program (ORF-RE).
            </Text>

            <Button
              as="a"
              href="https://site-cav.nextconlab.ca/"
              target="_blank"
              size="lg"
              borderRadius="16px"
              fontWeight="900"
              bg={useColorModeValue(
                "linear-gradient(135deg, rgba(49,83,105,0.10), rgba(220,36,44,0.10))",
                "linear-gradient(135deg, rgba(49,83,105,0.15), rgba(220,36,44,0.15))"
              )}
              color={useColorModeValue("#0F172A", "white")}
              border="1px solid"
              borderColor={softBorder}
              _hover={{ filter: "brightness(0.97)" }}
            >
              Visit SITE-CAV →
            </Button>
          </Box>

        </SimpleGrid>

        {/* Divider separating from Highlights */}
        <Divider mt={{ base: 4, md: 8 }} borderColor={softBorder} />
      </Container>
    </Box>


      {/* HIGHLIGHTS */}
      <Box py={{ base: 2, md: 4 }}>
        <Container maxW="1200px">
          <InlineCarousel
            images={highlights}
            title="Highlights"
            subtitle="Selected research outcomes, demos, and collaborations from NEXTCON Lab • SCVIC."
            autoPlay={true}
            intervalMs={3000}
            aspect={{ base: "16 / 10", md: "16 / 8" }}
            dotOnColor={accent2}
          />
        </Container>
      </Box>

      {/* MISSION / VISION / GOAL */}
      <Section
        title="Mission, Vision & Goal"
        subtitle=""
      >
		<SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
		  <ExpandableInfoCard
			label="Mission"
			summary="We pioneer cutting-edge research in communications and computing networks by integrating 6G, AI, edge intelligence, and cybersecurity to enable secure connectivity for smart vehicles and beyond."
			fullText="The mission of the Next Generation Communications and Computing Networks (NEXTCON) Research Laboratory and the Smart Connected Vehicles Innovation Centre (SCVIC) is to pioneer cutting-edge research and innovation in communications and computing technologies, integrating cybersecurity measures alongside 6G integration, Artificial Intelligence (AI), and edge intelligence. We aim to advance the state-of-the-art in next-generation networking solutions, enabling seamless connectivity for smart vehicles and beyond while prioritizing security. Through interdisciplinary collaboration, industry partnerships, and a commitment to high-quality research dissemination, we address complex challenges and drive transformative impact in the telecommunications, information technology, and automotive industries."
		  />

		  <ExpandableInfoCard
			label="Vision"
			summary="We envision secure, seamlessly integrated smart infrastructure and connected mobility powered by 6G capabilities, AI-driven insights, edge intelligence, and robust cybersecurity."
			fullText="Our vision is to create a future where smart and critical infrastructures and connected vehicles form a seamlessly integrated ecosystem, fortified by 6G capabilities, AI-driven insights, edge intelligence, and robust cybersecurity measures. At the NEXTCON Research Laboratory & SCVIC, we envision revolutionizing the way critical infrastructures (e.g., vehicular networks) communicate and interact with their environment, laying the foundation for autonomous driving, intelligent transportation systems, and novel mobility services. Through innovative research and development efforts, strategic industrial collaborations, and a determined focus on cybersecurity, we aim to establish ourselves as global leaders in shaping the future of connected mobility. Leveraging 6G advancements, AI, edge intelligence, and cybersecurity, we unlock new possibilities for telecommunications, information technologies and smart transportation, ensuring security is ingrained in every aspect of our advancements."
		  />

		  <ExpandableInfoCard
			label="Goal"
			summary="Our goal is to build secure, robust, and resilient 6G-ready networking and edge-AI solutions for smart connected critical infrastructures, supporting ultra-low latency, high reliability, and massive connectivity."
			fullText="Our primary goal is to push the boundaries of communications and computing technologies, emphasizing 6G integration, AI, edge intelligence, and cybersecurity, to enable the next generation of smart connected critical infrastructures. We prioritize developing secure, robust, and resilient networking solutions capable of supporting ultra-low latency, high reliability, and massive connectivity requirements of future critical infrastructure applications, leveraging the advancements of 6G networks while ensuring cybersecurity at every stage. Additionally, we leverage the power of AI and edge intelligence to optimize vehicle-to-everything (V2X) communication, intelligent traffic management, and enhanced driver assistance systems, paving the way for safer, more efficient, and sustainable mobility solutions. Through strategic industrial collaborations, we translate our research findings into real-world applications and innovations that benefit society, always keeping cybersecurity at the forefront. Furthermore, we commit to disseminating our research outcomes through high-quality publications in impactful journals and presentations at established conferences, ensuring our contributions are recognized and adopted by the global research community and industry stakeholders."
		  />
		</SimpleGrid>
      </Section>

      {/* WHAT WE DO */}
      <Section title="What We Do" subtitle="Core research themes that define NEXTCON Lab • SCVIC.">
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={5}>
          {[
            { t: "6G & Future Networks", d: "Architecture, protocols, and performance for next-gen connectivity." },
            { t: "Edge-Cloud Intelligence", d: "Low-latency inference, orchestration, and scalable deployment." },
            { t: "Connected & Autonomous Mobility", d: "V2X systems, cooperative perception, and safety-centric AI." },
            { t: "Security & Resilience", d: "Secure networking, robust ML, and defense against adversarial threats." },
          ].map((x) => (
            <Box
              key={x.t}
              border="1px solid"
              borderColor={softBorder}
              borderRadius="22px"
              p={6}
              bg={panelBg}
              boxShadow={useColorModeValue("0 10px 24px rgba(0,0,0,0.07)", "none")}
            >
              <Text fontWeight="900" color={titleColor} fontSize="lg" mb={2}>
                {x.t}
              </Text>
              <Text color={subColor} fontSize="md" lineHeight={1.75}>
                {x.d}
              </Text>
            </Box>
          ))}
        </SimpleGrid>
      </Section>

      {/* LAB PICTURES */}
      <Box pb={{ base: 14, md: 16 }}>
        <Container maxW="1200px">
          <InlineCarousel
            images={labPictures}
            title="Lab Pictures"
            subtitle="Testbeds, demonstrations, and research activities from the lab."
            autoPlay={true}
            intervalMs={3000}
            aspect={{ base: "16 / 10", md: "16 / 8" }}
            dotOnColor={accent2}
          />
        </Container>
      </Box>

      {/* FINAL CTA */}
      <Box
        py={{ base: 12, md: 16 }}
        bg={useColorModeValue("rgba(2,6,23,0.03)", "rgba(255,255,255,0.03)")}
        borderTop="1px solid"
        borderColor={softBorder}
      >
        <Container maxW="1200px">
          <Box
            border="1px solid"
            borderColor={softBorder}
            bg={panelBg}
            borderRadius="26px"
            p={{ base: 7, md: 10 }}
            boxShadow={useColorModeValue("0 16px 44px rgba(0,0,0,0.10)", "none")}
          >
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 6, md: 10 }} alignItems="center">
              <Stack spacing={3}>
                <Heading color={titleColor} fontSize={{ base: "xl", md: "2xl" }}>
                  Interested in collaboration?
                </Heading>
                <Text color={subColor} fontSize={{ base: "md", md: "lg" }} lineHeight={1.75}>
                  We welcome partnerships across academia and industry. Contact us to explore joint projects, student
                  opportunities, testbed experimentation, and technology transfer.
                </Text>
              </Stack>

              <Flex justify={{ base: "flex-start", md: "flex-end" }} gap={3} flexWrap="wrap">
                <Button
                  as={RouterLink}
                  to={`https://bkantarci.nextconlab.ca/`}
                  bg={accent2}
                  color="#06120A"
                  fontWeight="900"
                  size="lg"
                  borderRadius="16px"
                  target="_blank"
                  _hover={{ filter: "brightness(0.95)" }}
                >
                  Contact Director
                </Button>
                <Button
                  as={RouterLink}
                  to={`/members`}
                  variant="outline"
                  borderColor={softBorder}
                  size="lg"
                  borderRadius="16px"
                  fontWeight="900"
                >
                  Meet the Team
                </Button>
              </Flex>
            </SimpleGrid>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}